import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChange,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { List, Map, Set } from 'immutable';
import { isString, noop } from 'lodash';
import { BehaviorSubject, forkJoin, Observable, Subject } from 'rxjs';
import { v4 } from 'uuid';
import { EntityTypesEnum } from '../../../../app.enum';
import { ITimeEntry } from '../../../appointment-data/appointment-data.data';
import { AppointmentDataDispatcher } from '../../../appointment-data/appointment-data.dispatcher';
import { Appointment } from '../../../appointment-data/models/appointment.model';
import { AttachmentView } from '../../../attachments/attachment-view.model';
import { AttachmentBuilder } from '../../../attachments/attachment.builder';
import { AttachmentsStore } from '../../../attachments/attachments.store';
import { ClientDataBuilder } from '../../../client-data/client-data.builder';
import { Client } from '../../../client-data/client.model';
import { IConfirmDialogResult } from '../../../dialog/components/confirm-dialog/confirm-dialog.interface';
import { DialogService } from '../../../dialog/dialog.service';
import {
  ADD_CLIENT_ACTION,
  DocumentTypesEnum,
} from '../../../document-data/document-data.data';
import { Document } from '../../../document-data/document.model';
import { Billing } from '../../../document-data/sub-models/billing.model';
import { CalculationResult } from '../../../document-data/sub-models/calculation-result.model';
import { DocumentItem } from '../../../document-data/sub-models/document-item.model';
import { Shipping } from '../../../document-data/sub-models/shipping.model';
import { ALL_LIST_FILTER } from '../../../entity-data/entity-data.data';
import { ExpenseDataDispatcher } from '../../../expense-data/expense-data.dispatcher';
import { Expense } from '../../../expense-data/models/expense.model';
import { BannerCanvasTypesEnum } from '../../../features-data/features-data.data';
import { FeaturesDataStore } from '../../../features-data/features-data.store';
import { AccountQuotas } from '../../../features-data/sub-models/account-quotas.model';
import { Toggles } from '../../../features-data/sub-models/toggles.model';
import { File as AttachmentFile } from '../../../files/file.model';
import { GeolocationDataService } from '../../../geolocation-data/geolocation-data.service';
import { LocationInfo } from '../../../geolocation-data/models/location-info.model';
import { ItemDataDispatcher } from '../../../item-data/item-data.dispatcher';
import { Item } from '../../../item-data/item.model';
import { Settings } from '../../../settings-data/settings.model';
import { CompanySettings } from '../../../settings-data/sub-models/company-settings/company-settings.model';
import { Tax } from '../../../settings-data/sub-models/company-settings/tax.model';
import { WithholdingTax } from '../../../settings-data/sub-models/company-settings/withholding-tax.model';
import { CustomField } from '../../../settings-data/sub-models/custom-field.model';
import { DocumentNotes } from '../../../settings-data/sub-models/document-preset-settings/document-notes.model';
import { DocumentPresetSettings } from '../../../settings-data/sub-models/document-preset-settings/document-preset-settings.model';
import { GlobalBannerService } from '../../../shared/components/global-banner/global-banner.service';
import { IIcon } from '../../../shared/components/icon/icon.interface';
import { IconSizesEnum } from '../../../shared/components/icon/icons-sizes.enum';
import { ImageService } from '../../../shared/services/image/image.service';
import { LoggerService } from '../../../shared/services/logger/logger.service';
import { TrackedTime } from '../../../tracked-time-data/models/tracked-time.model';
import { TrackedTimeFiltersEnum } from '../../../tracked-time-data/tracked-time-data.data';
import { TrackedTimeDataDispatcher } from '../../../tracked-time-data/tracked-time-data.dispatcher';
import {
  I2gNgTrackingActionBuilder,
  ITrackingProperties,
} from 'i2g-ng-tracking';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingDialogNamesEnum,
  TrackingInputTypeValuesEnum,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { TrackingDispatcher } from '../../../tracking/tracking.dispatcher';
import { IAddAdditionalFieldsActionTrigger } from '../add-additional-fields/add-additional-fields.component';
import {
  AddClientActionsEnum,
  IAddClientActionTrigger,
  IAddClientData,
} from '../add-client/add-client.component';
import { IDocumentItemReorderInfo } from '../document-items-list/document-items-list.component';
import { DIALOG_TYPE as expensePickerDialog } from '../expense-picker-dialog/expense-picker-dialog.component';
import { DIALOG_TYPE as itemPickerDialog } from '../item-picker-dialog/item-picker-dialog.component';
import { DIALOG_TYPE as timeEntryPickerDialog } from '../time-entry-picker-dialog/time-entry-picker-dialog.component';
import { ContentCreateBuilder } from './content-create.builder';
import { ClientDataDispatcher } from '../../../client-data/client-data.dispatcher';
import { ToasterDispatcher } from '../../../toaster/toaster.dispatcher';
import { IToasterConfig, IToasterData } from '../../../toaster/toaster.data';
import { MessageToastComponent } from '../../../shared/components/message-toast/message-toast.component';
import {
  distinctUntilChanged,
  filter,
  finalize,
  map,
  shareReplay,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators';
import { IDocumentStore } from '../../../document-data/document-store.interface';
import { IDocumentDataDispatcher } from '../../../document-data/document-data-dispatcher.interface';
import { NavigationService } from '../../../navigation/navigation.service';
import { IDocumentDataBuilder } from '../../../document-data/document-data-builder.interface';

export enum ContentCreateModesEnum {
  none = 'none',
  edit_client = 'edit_client',
  edit_additional_fields = 'edit_additional_fields',
  edit_document_item = 'edit_document_item',
  add_document_item = 'add_document_item',
  edit_attachment = 'edit_attachment',
}

export const MAX_DOCUMENT_ITEMS = 200;
export const MAX_ATTACHMENTS = 10;
export const MAX_TIME_ENTRIES = 10;

@Component({
  selector: 'app-content-create',
  templateUrl: './content-create.component.html',
  styleUrls: ['./content-create.component.scss'],
  viewProviders: [ClientDataBuilder, ContentCreateBuilder],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContentCreateComponent implements OnInit, OnChanges, OnDestroy {
  @Input() documentDataDispatcher: IDocumentDataDispatcher;
  @Input() documentDataStore: IDocumentStore;
  @Input() documentDataBuilder: IDocumentDataBuilder;
  @Input() documentShipping: Shipping;
  @Input() documentItems: List<DocumentItem>;
  @Input() documentBilling: Billing;
  @Input() documentAdditionalFields: List<CustomField>;
  @Input() documentType: DocumentTypesEnum;
  @Input() documentNotes: DocumentNotes;
  @Input() documentPaymentDetails: string;
  @Input() accountSettings: Settings;
  @Input() documentTaxes: List<Tax>;
  @Input() documentWithholdingTax: WithholdingTax;
  @Input() documentSettings: DocumentPresetSettings;
  @Input() companySettings: CompanySettings;
  @Input() documentCalculationResult: CalculationResult;
  @Input() attachments: List<AttachmentView>;
  @Input() enableShipping: boolean;
  @Input() enablePaymentDetails: boolean;
  @Input() enableDiscount: boolean;
  @Input() activeItemReferenceId: string;
  @Input() featureToggles: Toggles;
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input() trackingObject: ITrackingObject;
  @Input() trackingAdditionalProperties: ITrackingProperties;

  @Output() onUpdateContentCreate: EventEmitter<
    Map<string, any>
  > = new EventEmitter<Map<string, any>>();
  @Output() onUpdateParentProperties: EventEmitter<
    Map<string, any>
  > = new EventEmitter<Map<string, any>>();
  @Output() onGrowthCta: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  onActiveItemEditMode: EventEmitter<string> = new EventEmitter<string>();

  @ViewChildren('sectionLabel,separator') labels: QueryList<ElementRef>;

  private _documentItemTrackedTimeIds: List<string>;
  private _documentItemAppointmentIds: List<string>;
  private _modeStream: BehaviorSubject<ContentCreateModesEnum>;
  private _modeStream$: Observable<ContentCreateModesEnum>;
  private _addClientActionTriggers: Subject<IAddClientActionTrigger>;
  private _addAdditionalFieldsActionTriggers: Subject<IAddAdditionalFieldsActionTrigger>;
  private _destroyed: Subject<boolean> = new Subject<boolean>();

  clientId: string;
  docType: string;
  documentNote: string;
  attachmentLimitErrorMessage: string;
  confirmDeleteTrackedTimeId: string;
  confirmDeleteAppointmentId: string;
  confirmUpdateClientId: string;
  hasClient: boolean;
  showShipping: boolean;
  hasAdditionalFieldValues: boolean;
  atDocumentItemLimit: boolean;
  atAttachmentLimit: boolean;
  showTimeTracking: boolean;
  showAppointments: boolean;
  maxDocumentItemsAllowed = MAX_DOCUMENT_ITEMS;
  maxAttachmentsAllowed = MAX_ATTACHMENTS;
  maxTimeEntriesAllowed = MAX_TIME_ENTRIES;
  addItemsIcon: IIcon;
  attachmentsIcon: IIcon;
  mode: ContentCreateModesEnum;
  documentItemParts: List<DocumentItem>;
  documentItemLabor: List<DocumentItem>;
  EntityTypesEnum: typeof EntityTypesEnum;
  ContentCreateModesEnum: typeof ContentCreateModesEnum;
  TrackingDialogNamesEnum: typeof TrackingDialogNamesEnum;
  addItemsAction: ITrackingAction;
  addExpensesAction: ITrackingAction;
  addTimeEntriesAction: ITrackingAction;
  addAppointmentsAction: ITrackingAction;
  addPhotosAction: ITrackingAction;
  deletedTrackedTimeAction: ITrackingAction;
  deletedAppointmentAction: ITrackingAction;
  document$: Observable<Document>;
  documentItemTimeEntryIds$: Observable<List<string>>;
  addClientActionTriggers$: Observable<IAddClientActionTrigger>;
  addAdditionalFieldsActionTriggers$: Observable<IAddAdditionalFieldsActionTrigger>;
  attachmentsInProgress$: Observable<List<AttachmentView>>;
  locationInfo$: Observable<LocationInfo>;
  showPaymentsDetailFooter$: Observable<boolean>;
  timeEntryPicker$: BehaviorSubject<EntityTypesEnum> = new BehaviorSubject<EntityTypesEnum>(
    EntityTypesEnum.tracked_time
  );

  constructor(
    private _route: ActivatedRoute,
    private _contentCreateBuilder: ContentCreateBuilder,
    private _attachmentBuilder: AttachmentBuilder,
    private _attachmentsStore: AttachmentsStore,
    private _featuresDataStore: FeaturesDataStore,
    private _expenseDataDispatcher: ExpenseDataDispatcher,
    private _clientDataDispatcher: ClientDataDispatcher,
    private _trackedTimeDataDispatcher: TrackedTimeDataDispatcher,
    private _appointmentDataDispatcher: AppointmentDataDispatcher,
    private _itemDataDispatcher: ItemDataDispatcher,
    private _toasterDispatcher: ToasterDispatcher,
    private _trackingDispatcher: TrackingDispatcher,
    private _geolocationDataService: GeolocationDataService,
    private _loggerService: LoggerService,
    private _dialogService: DialogService,
    private _imageService: ImageService,
    private _globalBannerService: GlobalBannerService,
    private _translateService: TranslateService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _navigationService: NavigationService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    const documentBillingChanges: SimpleChange = changes.documentBilling;
    const documentShippingChanges: SimpleChange = changes.documentShipping;
    const attachmentsChanges: SimpleChange = changes.attachments;
    const documentSettingsChanges: SimpleChange = changes.documentSettings;
    const documentNotesChanges: SimpleChange = changes.documentNotes;
    const documentTypeChanges: SimpleChange = changes.documentType;

    if (documentTypeChanges) {
      this.docType = this.documentType;
    }

    if (documentSettingsChanges && this.documentSettings) {
      this.showShipping = this.documentSettings.showShipping;
    }

    if (documentBillingChanges || documentShippingChanges) {
      this.hasClient =
        (this.documentBilling && this.documentBilling.hasValue()) ||
        (this.showShipping &&
          this.documentShipping &&
          this.documentShipping.hasValue());
    }

    if (documentBillingChanges) {
      this.clientId =
        this.documentBilling &&
        this.documentBilling.reference &&
        this.documentBilling.reference.hasValue()
          ? this.documentBilling.reference.id
          : null;
    }

    if (changes.documentItems && this.documentItems) {
      this.documentItemParts = this.documentItems.filter((documentItem) =>
        documentItem.isPart()
      ) as List<DocumentItem>;
      this.documentItemLabor = this.documentItems.filter((documentItem) =>
        documentItem.isLabor()
      ) as List<DocumentItem>;
      this.atDocumentItemLimit =
        this.documentItems.size >= this.maxDocumentItemsAllowed;

      this._documentItemTrackedTimeIds = this.documentItems
        .filter((documentItem) => documentItem.isTrackedTimeReference())
        .map((documentItem) => documentItem.referenceId) as List<string>;
      this._documentItemAppointmentIds = this.documentItems
        .filter((documentItem) => documentItem.isAppointmentReference())
        .map((documentItem) => documentItem.referenceId) as List<string>;
    }

    if (attachmentsChanges && !attachmentsChanges.isFirstChange()) {
      this.trackNewAttachmentsChanges(
        attachmentsChanges.previousValue,
        this.attachments
      );

      this.changeAttachmentList(this.attachments);
    }

    if (attachmentsChanges && this.attachments) {
      this.atAttachmentLimit =
        this.attachments.size >= this.maxAttachmentsAllowed;
    }

    if (documentNotesChanges && this.documentNotes) {
      this.documentNote = this.documentNotes.useDefault
        ? this.documentNotes.defaultNotes
        : this.documentNotes.noteFor(this.documentType);
    }

    if (documentTypeChanges && this.documentType) {
      this.attachmentLimitErrorMessage = this._translateService.instant(
        `DOCUMENT.${this.documentType}.WARNINGS.BULK_SELECT_ATTACHMENT_LIMIT`.toUpperCase(),
        { maxNumber: this.maxAttachmentsAllowed }
      );
    }

    if (changes.documentAdditionalFields) {
      this.hasAdditionalFieldValues = (
        this.documentAdditionalFields || List([])
      ).some((field: CustomField) => !!field.value);
    }

    if (changes.trackingObject) {
      this._attachmentsStore.setTrackingObject(this.trackingObject);
    }

    if (changes.featureToggles || changes.documentType) {
      const isInvoice = this.documentType === DocumentTypesEnum.invoice;
      const isNotForbidden = (feature) =>
        this.featureToggles && !this.featureToggles.isForbidden(feature);

      this.showTimeTracking =
        isInvoice && isNotForbidden(Toggles.KEYS.TIME_TRACKING);
      this.showAppointments =
        isInvoice && isNotForbidden(Toggles.KEYS.APPOINTMENTS);
    }
  }

  ngOnInit() {
    this._modeStream = new BehaviorSubject<ContentCreateModesEnum>(
      ContentCreateModesEnum.none
    );
    this._modeStream$ = this._modeStream.asObservable();
    this._addClientActionTriggers = new Subject<IAddClientActionTrigger>();
    this._addAdditionalFieldsActionTriggers = new Subject<IAddAdditionalFieldsActionTrigger>();

    this.locationInfo$ = this._geolocationDataService.getLocationInfo();
    this.addClientActionTriggers$ = this._addClientActionTriggers.asObservable();
    this.addAdditionalFieldsActionTriggers$ = this._addAdditionalFieldsActionTriggers.asObservable();
    this.addItemsIcon = {
      name: 'add-items',
      size: IconSizesEnum.medium,
    };
    this.attachmentsIcon = {
      name: 'add-photo',
      size: IconSizesEnum.medium,
    };
    this.attachmentsInProgress$ = this._attachmentsStore.inProgress$;
    this.ContentCreateModesEnum = ContentCreateModesEnum;
    this.EntityTypesEnum = EntityTypesEnum;
    this.TrackingDialogNamesEnum = TrackingDialogNamesEnum;
    this.confirmDeleteTrackedTimeId = v4();
    this.confirmDeleteAppointmentId = v4();
    this.confirmUpdateClientId = v4();
    this.addItemsAction = this._trackingActionBuilder.buildButtonTapped(
      'add_item',
      this.trackingScreenName
    );
    this.addExpensesAction = this._trackingActionBuilder.buildButtonTapped(
      'add_expense',
      this.trackingScreenName
    );
    this.addTimeEntriesAction = this._trackingActionBuilder.buildButtonTapped(
      'add_time_entry',
      this.trackingScreenName
    );
    this.addAppointmentsAction = this._trackingActionBuilder.buildButtonTapped(
      'add_appointment',
      this.trackingScreenName
    );
    this.addPhotosAction = this._trackingActionBuilder.buildButtonTapped(
      'add_photo',
      this.trackingScreenName
    );
    this.deletedTrackedTimeAction = this._trackingActionBuilder.buildButtonTapped(
      'delete_tracked_time',
      this.trackingScreenName
    );
    this.deletedAppointmentAction = this._trackingActionBuilder.buildButtonTapped(
      'delete_appointment',
      this.trackingScreenName
    );

    // TODO [refactor]: remove documentDataStore dependency
    this.document$ = this.documentDataStore
      .getValidEditingEntity()
      .pipe(shareReplay(1));

    this.document$
      .pipe(take(1), takeUntil(this._destroyed))
      .subscribe((document: Document) =>
        this._attachmentsStore.setAttachments(document.attachments, false)
      );

    this.document$
      .pipe(
        filter((x) => !!x),
        take(1),
        switchMap(() => this._route.queryParamMap),
        filter(
          (params: ParamMap) => params.get('action') === ADD_CLIENT_ACTION
        ),
        takeUntil(this._destroyed)
      )
      .subscribe(() => {
        this.editClient();

        this._navigationService.goTo([], {
          queryParams: {
            action: null,
          },
          queryParamsHandling: 'merge',
        });
      });

    this._modeStream$
      .pipe(takeUntil(this._destroyed))
      .subscribe((mode: ContentCreateModesEnum) => {
        this.mode = mode;

        this._changeDetectorRef.markForCheck();
      });

    this.documentItemTimeEntryIds$ = this.timeEntryPicker$.pipe(
      map((timeEntryType: EntityTypesEnum) =>
        timeEntryType === EntityTypesEnum.appointment
          ? this._documentItemAppointmentIds
          : this._documentItemTrackedTimeIds
      )
    );

    this.showPaymentsDetailFooter$ = this.document$.pipe(
      map((document: Document) => {
        return (
          this.enablePaymentDetails &&
          this.documentPaymentDetails &&
          this.companySettings.payments.bankTransferEnabled() &&
          !document.settings.bankTransferDisabled
        );
      }),
      distinctUntilChanged()
    );
  }

  resetMode() {
    this._modeStream.next(ContentCreateModesEnum.none);
  }

  /****
   CLIENT
   ****/
  onExistingClientEdit(flag: boolean) {
    if (!flag) {
      if (this._modeStream.value === ContentCreateModesEnum.edit_client) {
        this.resetMode();
      }
    } else {
      this._modeStream.next(ContentCreateModesEnum.edit_client);
    }
  }

  editClient() {
    this._loggerService.log('entering edit client mode in the document');

    this.onExistingClientEdit(true);

    // setTimout here so subscriber on <app-add-client> is ready when action is emitted
    setTimeout(() =>
      this._addClientActionTriggers.next({
        action: AddClientActionsEnum.show_form,
      })
    );
  }

  deleteClient() {
    this._loggerService.log('deleting client from the document');

    if (this.documentBilling.hasValue()) {
      const clientRef = this.documentBilling.reference;

      if (clientRef) {
        const clientUnassignedAction: ITrackingAction = this._trackingActionBuilder.buildInputUnassigned(
          clientRef.id,
          clientRef.referenceLocalId,
          TrackingInputTypeValuesEnum.client
        );

        this._trackingDispatcher.trackEvent(
          this.trackingObject,
          clientUnassignedAction,
          this.trackingAdditionalProperties
        );
      }
    }

    this.onExistingClientEdit(false);
    this._addClientActionTriggers.next({
      action: AddClientActionsEnum.close_form,
    });

    this.onUpdateContentCreate.emit(
      this._contentCreateBuilder.buildDocumentContentPropsFromDeleteClient()
    );
  }

  updateClient(addClientData: IAddClientData) {
    const {
      reference,
      hasEmailChanges,
      hasMobileChanges,
      achCreditBankDetails,
    } = addClientData;

    this.onUpdateContentCreate.emit(
      this._contentCreateBuilder.buildDocumentContentPropsFromClientData(
        addClientData
      )
    );

    if (reference) {
      const referenceId = reference.id;

      if ((hasEmailChanges || hasMobileChanges) && referenceId) {
        this._dialogService.openConfirmDialog(
          this.confirmUpdateClientId,
          addClientData
        );
      }

      if (hasMobileChanges && referenceId) {
        this._dialogService.openConfirmDialog(
          this.confirmUpdateClientId,
          addClientData
        );
      }

      const clientAssignedAction: ITrackingAction = this._trackingActionBuilder.buildInputAssigned(
        referenceId,
        reference.referenceLocalId,
        TrackingInputTypeValuesEnum.client
      );

      this._trackingDispatcher.trackEvent(
        this.trackingObject,
        clientAssignedAction,
        this.trackingAdditionalProperties
      );
    }

    if (achCreditBankDetails) {
      this.onUpdateParentProperties.emit(
        this._contentCreateBuilder.buildAchCreditBankDetailsProps(
          achCreditBankDetails
        )
      );
    }
  }

  onConfirmUpdateClientEmail({ result, data }: IConfirmDialogResult) {
    if (result) {
      const { reference, billingInfo }: IAddClientData = data;

      this._clientDataDispatcher
        .updateEntity(
          reference.id,
          this._contentCreateBuilder.buildClientEmailProps(billingInfo)
        )
        .pipe(takeUntil(this._destroyed))
        .subscribe(
          (client: Client) => {
            const label = this._translateService.instant(
              'DIALOG.UPDATE_CLIENT.SUCCESS_TOAST'
            );
            const toastData: IToasterData = { label, containsHtml: true };
            const toastConfig: IToasterConfig = {
              type: 'update-client-email',
              component: MessageToastComponent,
              data: toastData,
            };
            this._toasterDispatcher.showToast(toastConfig);
          },
          (error) => this._globalBannerService.showSystemErrorBanner(error)
        );
    }
  }

  saveClientToList(client: Client) {
    this._clientDataDispatcher
      .saveNewEntity(client)
      .pipe(filter((x) => !!x))
      .subscribe((savedClient: Client) => {
        this.documentDataDispatcher.setEditingEntityField(
          [Document.CONTENT_KEYS.BILLING, Billing.KEYS.REFERENCE],
          this._contentCreateBuilder.buildClientReference(savedClient)
        );
      }, noop);
  }

  /****
   ADDITIONAL FIELDS
   ****/
  onExistingAdditionalFieldsEdit(flag: boolean) {
    if (!flag) {
      if (
        this._modeStream.value === ContentCreateModesEnum.edit_additional_fields
      ) {
        this.resetMode();
      }
    } else {
      this._modeStream.next(ContentCreateModesEnum.edit_additional_fields);
    }
  }

  updateAdditionalFields(additionalFieldsProps: List<Map<string, any>>) {
    this.onUpdateContentCreate.emit(
      this._contentCreateBuilder.buildDocumentContentPropsFromAdditionalFields(
        additionalFieldsProps
      )
    );
  }

  /****
   NOTE
   ****/
  changeNote(note: string) {
    this.onUpdateContentCreate.emit(
      this._contentCreateBuilder.buildDocumentContentPropsFromNote(
        this.documentNotes.useDefault,
        this.documentType,
        note
      )
    );
  }

  /****
   PAYMENT DETAILS
   ****/
  changePaymentDetails(paymentDetails: string) {
    this.onUpdateContentCreate.emit(
      this._contentCreateBuilder.buildDocumentContentPropsFromPaymentDetails(
        paymentDetails
      )
    );
  }

  /****
   DOCUMENT ITEMS
   ****/
  enterAddItemMode() {
    this._modeStream.next(ContentCreateModesEnum.add_document_item);
  }

  onDeleteDocumentItem(documentItem: DocumentItem) {
    if (documentItem.isTrackedTimeReference()) {
      this._dialogService.openConfirmDialog(
        this.confirmDeleteTrackedTimeId,
        documentItem
      );
    } else if (documentItem.isAppointmentReference()) {
      this._dialogService.openConfirmDialog(
        this.confirmDeleteAppointmentId,
        documentItem
      );
    } else {
      this.deleteDocumentItem(documentItem);
    }
  }

  // TODO [refactor]: move this logic to effects
  onConfirmDeleteDocumentItem({ result, data }: IConfirmDialogResult) {
    if (result) {
      const documentItem = data as DocumentItem;

      if (documentItem.isTrackedTimeReference()) {
        this._trackedTimeDataDispatcher.clearList(
          TrackedTimeFiltersEnum.unbilled
        );
        this._trackedTimeDataDispatcher.clearList(
          TrackedTimeFiltersEnum.billed
        );
        this._trackedTimeDataDispatcher.clearFilters(
          TrackedTimeFiltersEnum.unbilled
        );
        this._trackedTimeDataDispatcher.clearFilters(
          TrackedTimeFiltersEnum.billed
        );
      } else if (documentItem.isAppointmentReference()) {
        this._appointmentDataDispatcher.clearList(ALL_LIST_FILTER);
      }

      this.deleteDocumentItem(documentItem);
    }
  }

  deleteDocumentItem(documentItem: DocumentItem) {
    this.documentDataDispatcher.deleteDocumentItemFromEditing(documentItem);

    let inputType;

    if (documentItem.isExpenseReference()) {
      inputType = TrackingInputTypeValuesEnum.expense;

      this._expenseDataDispatcher
        .getEntityById(documentItem.referenceId)
        .pipe(
          takeUntil(this._destroyed),
          filter((x) => !!x)
        )
        .subscribe((expense: Expense) => {
          const attachmentDeleted = this._trackingActionBuilder.buildInputUnassigned(
            expense.fileId,
            null,
            TrackingInputTypeValuesEnum.attachment
          );

          this._trackingDispatcher.trackEvent(
            this.trackingObject,
            attachmentDeleted,
            this.trackingAdditionalProperties
          );

          this._attachmentsStore.deleteAttachmentById(expense.fileId);
        });
    } else if (documentItem.isAppointmentReference()) {
      inputType = TrackingInputTypeValuesEnum.appointment;
    } else if (documentItem.isTrackedTimeReference()) {
      inputType = TrackingInputTypeValuesEnum.tracked_time;
    } else {
      inputType = TrackingInputTypeValuesEnum.item;
    }

    const itemUnassignedAction: ITrackingAction = this._trackingActionBuilder.buildInputUnassigned(
      documentItem.id,
      documentItem.localId,
      inputType
    );

    this._trackingDispatcher.trackEvent(
      this.trackingObject,
      itemUnassignedAction,
      this.trackingAdditionalProperties
    );

    this.onDocumentItemEdit(false);
  }

  saveItemToList(documentItem: DocumentItem) {
    const item: Item = this._contentCreateBuilder.buildItemFromDocumentItem(
      documentItem,
      this.accountSettings,
      this.companySettings.productNameEnabled
    );

    this._itemDataDispatcher
      .saveNewEntity(item)
      .pipe(filter((x) => !!x))
      .subscribe((savedItem: Item) => {
        const newDocumentItem: DocumentItem = documentItem.set(
          DocumentItem.KEYS.REFERENCE,
          this._contentCreateBuilder.buildItemReference(savedItem)
        ) as DocumentItem;

        this.documentDataDispatcher.updateDocumentItemInEditing(
          newDocumentItem
        );
      });
  }

  updateDocumentItem(documentItem: DocumentItem) {
    this.documentDataDispatcher.updateDocumentItemInEditing(documentItem);

    const inputType = documentItem.isExpenseReference()
      ? TrackingInputTypeValuesEnum.expense
      : TrackingInputTypeValuesEnum.item;

    const itemAssignedAction: ITrackingAction = this._trackingActionBuilder.buildInputAssigned(
      documentItem.id,
      documentItem.localId,
      inputType
    );

    this._trackingDispatcher.trackEvent(
      this.trackingObject,
      itemAssignedAction,
      this.trackingAdditionalProperties
    );
  }

  onDocumentItemEdit(flag: boolean) {
    if (!flag) {
      if (
        this._modeStream.value === ContentCreateModesEnum.edit_document_item
      ) {
        this.resetMode();
      }
    } else {
      this._modeStream.next(ContentCreateModesEnum.edit_document_item);
    }
  }

  onReorderDocumentItem(reorderInfo: IDocumentItemReorderInfo) {
    this.documentDataDispatcher.moveDocumentItemInEditing(
      reorderInfo.oldIndex,
      reorderInfo.newIndex,
      reorderInfo.isPart
    );
  }

  addDocumentItem(documentItem: DocumentItem) {
    const trackingInputType: TrackingInputTypeValuesEnum = documentItem.isExpenseReference()
      ? TrackingInputTypeValuesEnum.expense
      : TrackingInputTypeValuesEnum.item;
    const itemAssignedAction: ITrackingAction = this._trackingActionBuilder.buildInputAssigned(
      documentItem.id,
      documentItem.localId,
      trackingInputType
    );

    this._trackingDispatcher.trackEvent(
      this.trackingObject,
      itemAssignedAction,
      this.trackingAdditionalProperties
    );

    this.documentDataDispatcher.addDocumentItemToEditing(documentItem);

    this.resetMode();
  }

  batchAddDocumentItems(documentItems: List<DocumentItem>) {
    this.documentDataDispatcher.batchAddDocumentItemsToEditing(documentItems);
  }

  openItemPickerDialog() {
    this.resetMode();
    this._dialogService.openDialog({ type: itemPickerDialog });
  }

  addItem(item: Item) {
    const documentItemProps = this._contentCreateBuilder.buildDocumentItemPropsFromItem(
      this.documentType,
      item,
      this.companySettings.productNameEnabled
    );
    const documentItem: DocumentItem = this.documentDataBuilder.buildDocumentItem(
      documentItemProps,
      this.documentTaxes,
      this.documentWithholdingTax
    );

    this.addDocumentItem(documentItem);
  }

  addItems(items: List<Item>) {
    const documentItems: List<DocumentItem> = items.map((item) => {
      const documentItemProps = this._contentCreateBuilder.buildDocumentItemPropsFromItem(
        this.documentType,
        item,
        this.companySettings.productNameEnabled
      );

      return this.documentDataBuilder.buildDocumentItem(
        documentItemProps,
        this.documentTaxes,
        this.documentWithholdingTax
      );
    }) as List<DocumentItem>;

    this.batchAddDocumentItems(documentItems);
  }

  openExpensePickerDialog() {
    this._dialogService.openDialog({ type: expensePickerDialog });
  }

  addExpenses(expenses: List<Expense>) {
    const documentItems: List<DocumentItem> = expenses.map((expense) => {
      const documentItemProps = this._contentCreateBuilder.buildDocumentItemPropsFromExpense(
        expense,
        this.companySettings.productNameEnabled
      );

      return this.documentDataBuilder.buildDocumentItem(
        documentItemProps,
        this.documentTaxes,
        this.documentWithholdingTax
      );
    }) as List<DocumentItem>;

    this.batchAddDocumentItems(documentItems);

    const expensesWithAttachment = expenses.filter((expense: Expense) =>
      Boolean(expense.file)
    );

    if (
      this.attachments.size + expensesWithAttachment.size <=
      this.maxAttachmentsAllowed
    ) {
      expensesWithAttachment.forEach((expense) => {
        this.addAttachmentFromExpense(expense.description, expense.file);
      });
    }
  }

  openTimeEntriesPickerDialog(timeEntryType: EntityTypesEnum) {
    this.timeEntryPicker$.next(timeEntryType);

    // open dialog in next cycle so initial values have enough time to be updated
    setTimeout(() =>
      this._dialogService.openDialog({ type: timeEntryPickerDialog })
    );
  }

  addTimeEntries(timeEntries: List<ITimeEntry>) {
    const count = timeEntries.size;

    if (count > 0) {
      const isAppointment = timeEntries.get(0) instanceof Appointment;
      const productNameEnabled = this.companySettings.productNameEnabled;
      const showQuantityAndRate = this.documentSettings.showQuantityAndRate;
      const documentItems: List<DocumentItem> = timeEntries.map((timeEntry) => {
        const documentItemProps = isAppointment
          ? this._contentCreateBuilder.buildDocumentItemPropsFromAppointment(
              timeEntry as Appointment,
              productNameEnabled,
              showQuantityAndRate
            )
          : this._contentCreateBuilder.buildDocumentItemPropsFromTrackedTime(
              timeEntry as TrackedTime,
              productNameEnabled,
              showQuantityAndRate
            );

        return this.documentDataBuilder.buildDocumentItem(
          documentItemProps,
          this.documentTaxes,
          this.documentWithholdingTax
        );
      }) as List<DocumentItem>;
      const clientIds: Set<string> = timeEntries
        .map((trackedTime: TrackedTime) => trackedTime.clientReference.id)
        .toSet();

      if (!this.documentBilling.hasValue() && clientIds.size === 1) {
        this._dialogService.openLoadingDialog();

        this._clientDataDispatcher
          .getEntityById(clientIds.first())
          .pipe(
            takeUntil(this._destroyed),
            finalize(() => this._dialogService.closeLoadingDialog())
          )
          .subscribe((client: Client) => {
            if (client && !client.isDeleted()) {
              this.onUpdateContentCreate.emit(
                this._contentCreateBuilder.buildDocumentContentPropsFromClient(
                  client
                )
              );
            }

            this.batchAddDocumentItems(documentItems);
          });
      } else {
        this.batchAddDocumentItems(documentItems);
      }

      const inputType = isAppointment
        ? TrackingInputTypeValuesEnum.appointment
        : TrackingInputTypeValuesEnum.tracked_time;
      const bulkAssignedAction: ITrackingAction = this._trackingActionBuilder.buildInputAssignedBulk(
        inputType,
        count
      );

      this._trackingDispatcher.trackEvent(
        this.trackingObject,
        bulkAssignedAction,
        this.trackingAdditionalProperties
      );

      // set timeout so list-items have been created before entering in edit mode
      setTimeout(() =>
        this.onActiveItemEditMode.emit(documentItems.last().referenceId)
      );
    }
  }

  /****
   ATTACHMENTS ITEMS
   ****/
  onExistingAttachmentEdit(flag: boolean) {
    if (!flag) {
      if (this._modeStream.value === ContentCreateModesEnum.edit_attachment) {
        this.resetMode();
      }
    } else {
      this._modeStream.next(ContentCreateModesEnum.edit_attachment);
    }
  }

  onAttachFiles(files: FileList) {
    if (this.attachments.size < this.maxAttachmentsAllowed) {
      const numberOfFiles: number = files.length;
      const numberOfAllowedAttachments: number =
        this.maxAttachmentsAllowed - this.attachments.size;
      let truncatedFiles: File[] | FileList;

      if (numberOfAllowedAttachments < numberOfFiles) {
        this._loggerService.log(
          `too many attachments. keeping ${numberOfAllowedAttachments} attachments out of ${numberOfFiles} attachments`
        );
        truncatedFiles = [];

        for (let i = 0; i < numberOfAllowedAttachments; i++) {
          truncatedFiles.push(files.item(i));
        }
      } else {
        truncatedFiles = files;
      }

      this._loggerService.log(
        'attaching files to document create:',
        truncatedFiles
      );

      this._imageService.optimizeFiles(truncatedFiles).subscribe(
        (optimizedFiles: Blob[]) =>
          this._attachmentsStore.addToQueue(optimizedFiles),
        (error) =>
          this._globalBannerService.showSystemErrorBanner(
            error,
            this._translateService.instant('ERROR.IMAGE_NOT_OPTIMIZED')
          )
      );
    }
  }

  onCancelAttachment(attachment: AttachmentView) {
    this._attachmentsStore.cancelUpload(attachment);
  }

  onRetryAttachment(attachment: AttachmentView) {
    this._attachmentsStore.retryUpload(attachment);
  }

  onDeleteAttachmentItem(attachment: AttachmentView) {
    const attachmentDeleted = this._trackingActionBuilder.buildInputUnassigned(
      attachment.id,
      null,
      TrackingInputTypeValuesEnum.attachment
    );

    this._trackingDispatcher.trackEvent(
      this.trackingObject,
      attachmentDeleted,
      this.trackingAdditionalProperties
    );

    this._attachmentsStore.deleteAttachmentById(attachment.id);
  }

  onUpdateAttachmentItem(attachment: AttachmentView) {
    this._attachmentsStore.updateAttachment(attachment);
  }

  changeAttachmentList(attachmentChanges: List<AttachmentView>) {
    const attachments = this._attachmentBuilder.buildAttachmentsListFromAttachmentsView(
      attachmentChanges
    );

    this.onUpdateContentCreate.emit(
      this._contentCreateBuilder.buildDocumentContentPropsFromAttachmentList(
        attachments
      )
    );
  }

  addAttachmentFromExpense(description: string, file: AttachmentFile) {
    const featureName = Toggles.KEYS.ATTACHMENT;

    forkJoin(
      this._featuresDataStore.isNoAccess(featureName),
      this._featuresDataStore.canCreate(AccountQuotas.KEYS.STORAGE)
    )
      .pipe(takeUntil(this._destroyed))
      .subscribe(([noAttachmentFeature, canCreateStorage]) => {
        if (noAttachmentFeature) {
          this._dialogService.openGrowthDialog(
            BannerCanvasTypesEnum.feature_lock,
            featureName,
            this.onGrowthCta.emit.bind(this.onGrowthCta)
          );
        } else if (!canCreateStorage) {
          this._dialogService.openGrowthDialog(
            BannerCanvasTypesEnum.quota_reached,
            AccountQuotas.KEYS.STORAGE,
            this.onGrowthCta.emit.bind(this.onGrowthCta)
          );
        } else if (!this.atAttachmentLimit) {
          let attachmentTitle = this._translateService.instant('EXPENSE.NAME');

          // From requirement V2E-632:
          // - Attachment Title = "Expense" - "[First 15 characters of description]"
          if (isString(description)) {
            attachmentTitle += ' - ' + description.substring(0, 15);
          }

          this._attachmentsStore.addAttachment(
            this._attachmentBuilder.buildAttachment(file, attachmentTitle)
          );
        }
      });
  }

  trackNewAttachmentsChanges(
    previousList: List<AttachmentView>,
    currentList: List<AttachmentView>
  ) {
    currentList
      .filter((attachment) => attachment.emitEvent)
      .toSet()
      .subtract(previousList)
      .map((attachment) =>
        this._trackingActionBuilder.buildInputAssigned(
          attachment.id,
          null,
          TrackingInputTypeValuesEnum.attachment
        )
      )
      .forEach((trackingAction) =>
        this._trackingDispatcher.trackEvent(
          this.trackingObject,
          trackingAction,
          this.trackingAdditionalProperties
        )
      );
  }

  ngOnDestroy() {
    this._dialogService.closeDialog({ type: itemPickerDialog });
    this._dialogService.closeDialog({ type: expensePickerDialog });
    this._dialogService.closeDialog({ type: timeEntryPickerDialog });

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
